import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import InstallAppBanner from '@/components/pwa/InstallAppBanner';
import {
  User, MapPin, Calendar, Stethoscope, Scissors,
  ToggleRight, Star, Sparkles, Wallet, MessageCircle, FileText,
  Newspaper, QrCode, Zap, Phone, BarChart3, ClipboardList, ShieldCheck,
  CalendarClock, CalendarCheck, Menu, X, ArrowLeft,
} from 'lucide-react';
import ClinicKitTab from '@/pages/ClinicKitTab';
import OfflineBanner from '@/components/OfflineBanner';
import CallsChatTab from '@/components/dashboard/CallsChatTab';
import PatientLogTab from '@/components/dashboard/PatientLogTab';
import ChatInboxTab from '@/components/dashboard/ChatInboxTab';
import IncomingCallModal from '@/components/dashboard/IncomingCallModal';
import NewDoctorOnboardingWizard from '@/components/dashboard/NewDoctorOnboardingWizard';
import DhfShareBell from '@/components/dashboard/DhfShareBell';
import {
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';
import LeadsTab from '@/components/dashboard/tabs/LeadsTab';
import SeoArticlesTab from '@/components/dashboard/tabs/SeoArticlesTab';
import ProfileTab from '@/components/dashboard/tabs/ProfileTab';
import LocationsTab from '@/components/dashboard/tabs/LocationsTab';
import ScheduleTab from '@/components/dashboard/tabs/ScheduleTab';
import SlotManagerTab from '@/components/dashboard/tabs/SlotManagerTab';
import ProceduresTab from '@/components/dashboard/tabs/ProceduresTab';
import CaseStudiesTab from '@/components/dashboard/tabs/CaseStudiesTab';
import AppointmentsTab from '@/components/dashboard/tabs/AppointmentsTab';
import WalletTab from '@/components/dashboard/tabs/WalletTab';
import VisibilityAcceleratorTab from '@/components/dashboard/tabs/VisibilityAcceleratorTab';
import ClinicBrandingTab from '@/components/dashboard/tabs/ClinicBrandingTab';
import TqsTab from '@/components/dashboard/tabs/TqsTab';
import AmountsTab from '@/components/dashboard/tabs/AmountsTab';
import ByAppointmentRequestsTab from '@/components/dashboard/tabs/ByAppointmentRequestsTab';
import DhfPaymentsTab from '@/components/dashboard/tabs/DhfPaymentsTab';
import useActivityCounters from '@/lib/useActivityCounters';
import useDashboardChime from '@/lib/useDashboardChime';

// Tabs ordered by the doctor's onboarding lifecycle and tagged with a group.
// `tagLabel` overrides the group's corner-tag text for a specific button.
const TABS = [
  // ── SETUP ──
  { key: 'profile', label: 'Profile', icon: User, group: 'setup' },
  { key: 'locations', label: 'Clinics & Hospital Infra', icon: MapPin, group: 'setup' },
  { key: 'schedule', label: 'Schedule', icon: Calendar, group: 'setup' },
  { key: 'slots', label: 'Slot Manager', icon: ToggleRight, group: 'setup' },
  { key: 'clinic_kit', label: 'Clinic Kit', icon: QrCode, group: 'setup' },
  { key: 'branding', label: 'Clinic Branding', icon: Sparkles, group: 'setup' },
  // ── SPECIALIST / CONTENT ──
  { key: 'procedures', label: 'Procedures', icon: Stethoscope, group: 'specialist' },
  { key: 'casestudies', label: 'Case Studies', icon: FileText, group: 'specialist' },
  { key: 'seo_articles', label: 'AI SEO Articles', icon: Newspaper, group: 'specialist', tagLabel: 'Specialist Publish' },
  // ── VISIBILITY ──
  { key: 'visibility', label: 'Visibility Boost', icon: Zap, group: 'visibility', premiumOnly: true },
  // ── PATIENTS ENGAGEMENT ──
  { key: 'chat_inbox', label: 'Chat Inbox', icon: MessageCircle, group: 'engagement' },
  { key: 'calls_chat', label: 'Calls & Chat', icon: Phone, group: 'engagement', turboOnly: true },
  { key: 'leads', label: 'My Leads', icon: BarChart3, group: 'engagement' },
  { key: 'appointment_requests', label: 'Appt. Rqsts', icon: CalendarClock, group: 'engagement' },
  { key: 'appointments', label: 'Appointments', icon: CalendarCheck, group: 'engagement' },
  { key: 'patient_log', label: 'Patient Log', icon: ClipboardList, group: 'engagement' },
  { key: 'surgical_suite', label: 'Surgical Suite', icon: Scissors, group: 'engagement', href: '/surgical-suite', tagLabel: 'IPD · New' },
  { key: 'tqs', label: 'Quality (TQI) Index', icon: Star, group: 'engagement' },
  // ── BUSINESS / PAYMENTS ──
  { key: 'amounts', label: 'Amounts', icon: Wallet, group: 'business' },
  { key: 'dhf_payments', label: 'DHF Payments', icon: ShieldCheck, group: 'business' },
  { key: 'wallet', label: 'Wallet', icon: Wallet, group: 'business' },
];

// Corner-tag label + colour per onboarding group.
const GROUP_META = {
  setup: { label: 'Setup', color: 'text-sky-700', faded: 'text-sky-300' },
  specialist: { label: 'Specialist', color: 'text-violet-700', faded: 'text-violet-300' },
  visibility: { label: 'Visibility', color: 'text-amber-700', faded: 'text-amber-300' },
  engagement: { label: 'Engagement', color: 'text-teal-700', faded: 'text-teal-300' },
  business: { label: 'Business', color: 'text-slate-500', faded: 'text-slate-300' },
};

export default function DoctorDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [specializations, setSpecializations] = useState([]);
  // Onboarding phase signal: setup is "complete" once the publish gate has
  // zero blocking items (same source the SetupBlocker uses). Drives the
  // per-button ● Active state in the grouped nav.
  const [setupDone, setSetupDone] = useState(false);
  // True while the new-doctor wizard is the focus. When true, the original
  // SetupBlocker, ScoreWidget, UpgradeBanner and the tab bar are hidden so
  // the doctor sees ONLY the 5-step "Start here" card. They unlock the
  // moment the wizard collapses (profile becomes publishable OR doctor
  // taps "Skip for now").
  const [wizardActive, setWizardActive] = useState(false);
  // Desktop: collapse the secondary (setup/content/visibility) row behind a
  // ☰ toggle. Mobile/tablet: single-pane — tapping a tab hides the whole
  // button grid and shows only that tab's content + a Back bar.
  const [row2Open, setRow2Open] = useState(false);
  const [showMobileContent, setShowMobileContent] = useState(false);

  // Activity-counter polling: green pulsing dot + count badge per tab.
  // Polls every 30 s; resets at midnight local time.
  const counters = useActivityCounters(true);

  // Audible chime + immediate counter refresh when a new lead or new
  // clinic check-in push arrives — the doctor hears it AND sees the
  // dot/badge update without waiting for the next 30s poll.
  useDashboardChime(() => { counters.refresh(); });

  // Acknowledge a tab when the doctor clicks into it — clears the dot
  // but keeps the count badge visible until midnight rollover (per spec).
  const handleTabClick = (key) => {
    const tab = TABS.find((t) => t.key === key);
    if (tab && tab.href) {
      navigate(tab.href);
      return;
    }
    setActiveTab(key);
    counters.acknowledge(key);
    // On mobile/tablet, switch to the single content pane (hides the grid).
    setShowMobileContent(true);
  };

  // Auto-heal push subscription the moment a doctor lands on the dashboard.
  // After a PWA uninstall+reinstall, the browser keeps `permission==='granted'`
  // but the new SW has no PushManager subscription yet — so the backend's
  // stored subscription points at a dead FCM endpoint and login OTPs go
  // nowhere. Calling `subscribe()` here re-registers a fresh endpoint
  // automatically — no doctor action required, no Enable button to find.
  // Runs at the top level (not just inside ProfileTab) so it fires on every
  // initial dashboard render regardless of which tab is currently active.
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, permission: pushPermission, subscribe: pushSubscribe } = usePushNotifications();
  useEffect(() => {
    if (!user || loading) return;
    if (!pushSupported || pushSubscribed) return;
    if (pushPermission === 'denied') return;
    pushSubscribe().catch(() => {});
  }, [user, loading, pushSupported, pushSubscribed, pushPermission, pushSubscribe]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'doctor') { navigate('/'); return; }
    api.get('/specializations').then(r => setSpecializations(r.data)).catch(() => {});
    api.get('/dashboard/score').then(r => setSetupDone(((r.data?.blocking) || []).length === 0)).catch(() => {});
  }, [user, loading, navigate]);

  // Block search engines from indexing the authenticated dashboard. Belt-and-
  // braces with robots.txt — protects against accidental token-in-URL leaks.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  if (loading || !user || user.role !== 'doctor') return null;

  return (
    <div className="min-h-screen bg-slate-50/50" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <OfflineBanner />
      <InstallAppBanner />
      <div className="w-full px-4 sm:px-6 md:px-12 lg:px-20 xl:px-28 pt-3 pb-8 lg:pt-8">
        <div className="relative flex items-start justify-between gap-2 mb-3 lg:mb-6 lg:items-center lg:justify-center lg:min-h-[2.25rem]">
          <div className="flex-1 min-w-0 lg:flex-none lg:text-center">
            <p className="lg:hidden text-slate-500 font-semibold text-[12.5px] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{user.name ? `${user.name}'s` : ''}</p>
            <h1 className="text-slate-900 font-semibold leading-[1.12] text-[21px] lg:text-2xl lg:font-medium lg:leading-normal" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="lg:hidden">Clinical Practice Dashboard</span>
              <span className="hidden lg:inline">{user.name ? `${user.name}'s Clinical Practice Dashboard` : 'Clinical Practice Dashboard'}</span>
            </h1>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 lg:gap-3 lg:absolute lg:right-0">
            {/* Turbo-only: notification bell for files patients shared with other doctors */}
            {user.tier === 'turbo' && <DhfShareBell />}
            {/* Tier badge + upgrade band — free/premium doctors get a one-tap
                jump to /upgrade. Hidden for Turbo (top tier). */}
            <TierBadgeWithUpgrade tier={user.tier || 'free'} />
          </div>
        </div>

        <WhatsNewLaunchBanner setActiveTab={setActiveTab} />
        <NewDoctorOnboardingWizard
          tier={user.tier}
          slug={user.slug}
          onSetActiveTab={setActiveTab}
          onWizardActive={setWizardActive}
        />
        {!wizardActive && <SetupBlocker setActiveTab={setActiveTab} />}
        {!wizardActive && <WebsiteScoreWidget />}

        {!wizardActive && (user.tier !== 'premium' && user.tier !== 'turbo') && <UpgradeBanner />}
        {!wizardActive && <SubscriptionWarning />}

        {!wizardActive && (
          <div className={`${showMobileContent ? 'hidden' : 'block'} lg:block`} data-testid="dashboard-tabs-wrapper">
          {(() => {
          const visibleTabs = TABS.filter(t => !t.premiumOnly || user.tier === 'premium' || user.tier === 'turbo');
          const OUTCOME_GROUPS = ['engagement', 'business'];
          const SETUP_GROUPS = ['setup', 'specialist', 'visibility'];

          // One button renderer reused across layouts. `demoted` visually
          // mutes the Row-2 (setup/content) buttons once onboarding is done.
          const renderTabButton = (tab, idx, arr, demoted) => {
            const { key, label, icon: Icon, premiumOnly, group, tagLabel } = tab;
            const { count, hasUnseen } = counters.statusFor(key);
            const isActive = activeTab === key;
            const meta = GROUP_META[group] || GROUP_META.business;
            const prev = arr[idx - 1];
            const showSep = prev && prev.group !== group;
            const tagFaded = group === 'setup' && setupDone;
            const tagColorClass = isActive ? 'text-white/70' : (tagFaded ? meta.faded : meta.color);
            const statusColorClass = isActive
              ? (setupDone ? 'text-emerald-200' : 'text-white/50')
              : (setupDone ? 'text-emerald-600' : 'text-slate-300');
            return (
              <Fragment key={key}>
                {showSep && <span className="w-px self-stretch bg-slate-200 mx-1.5" aria-hidden="true" />}
                <button
                  onClick={() => handleTabClick(key)}
                  className={`relative flex items-center gap-2 rounded-xl font-semibold whitespace-nowrap transition-colors ${demoted ? 'pt-3.5 pb-2.5 px-3' : 'pt-4 pb-3 px-3.5 sm:px-4'} ${isActive ? 'bg-sky-600 text-white shadow-sm' : demoted ? 'bg-slate-50/70 text-slate-500 hover:bg-sky-50 border border-slate-200' : 'bg-white text-slate-700 hover:bg-sky-50 border border-slate-200'} ${premiumOnly ? 'ring-1 ring-amber-300' : ''}`}
                  style={{ fontSize: demoted ? 'clamp(0.78rem, 0.9vw, 0.85rem)' : 'clamp(0.85rem, 1vw, 0.95rem)' }}
                  data-testid={`tab-${key}`}
                >
                  <span className={`absolute top-1 left-2.5 text-[8px] font-bold uppercase tracking-[0.1em] leading-none ${tagColorClass}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {tagLabel || meta.label}
                  </span>
                  <Icon size={demoted ? 15 : 17} /> {label}
                  <span className={`absolute bottom-0.5 right-2 text-[7.5px] font-extrabold uppercase tracking-[0.08em] leading-none ${statusColorClass}`}>
                    ● Active
                  </span>
                  {count > 0 && (
                    <span
                      className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-white text-sky-700' : 'bg-emerald-100 text-emerald-800 border border-emerald-300'}`}
                      title={`${count} new today`}
                      data-testid={`tab-count-${key}`}
                    >
                      {count}
                    </span>
                  )}
                  {hasUnseen && (
                    <span
                      aria-label="New activity"
                      className="absolute -top-1 -right-1 inline-flex h-2.5 w-2.5"
                      data-testid={`tab-dot-${key}`}
                    >
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                    </span>
                  )}
                </button>
              </Fragment>
            );
          };

          const layerLabel = 'text-[0.62rem] font-bold uppercase tracking-[0.16em] ml-0.5';

          // Before setup is complete: keep the lifecycle order (Setup first).
          if (!setupDone) {
            return (
              <div className="flex flex-wrap items-stretch gap-1.5 mb-6 pb-1" data-testid="dashboard-tabs">
                {visibleTabs.map((t, i, arr) => renderTabButton(t, i, arr, false))}
              </div>
            );
          }

          // After setup: promote outcome tabs to Row 1; the setup/content/
          // visibility group collapses behind a ☰ toggle (right-aligned).
          const row1 = visibleTabs.filter(t => OUTCOME_GROUPS.includes(t.group));
          const row2 = visibleTabs.filter(t => SETUP_GROUPS.includes(t.group));
          return (
            <div className="mb-6" data-testid="dashboard-tabs">
              <p className={`text-slate-400 ${layerLabel} mb-1.5`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                Patients Engagement &amp; Business / Payments
              </p>
              <div className="flex flex-wrap items-stretch gap-1.5" data-testid="dashboard-tabs-row1">
                {row1.map((t, i, arr) => renderTabButton(t, i, arr, false))}
                <button
                  onClick={() => setRow2Open(o => !o)}
                  className={`ml-auto self-stretch flex items-center gap-2 px-3.5 rounded-xl border font-semibold transition-colors ${row2Open ? 'bg-slate-100 text-slate-700 border-slate-300' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  title="Setup, content & visibility"
                  aria-expanded={row2Open}
                  data-testid="row2-toggle-btn"
                >
                  <Menu size={18} />
                </button>
              </div>
              {row2Open && (
                <div className="mt-4 border-t border-dashed border-slate-200 pt-3" data-testid="dashboard-tabs-row2">
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-slate-400/80 ${layerLabel}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Setup &amp; Content &amp; Visibility · done, edit anytime
                    </p>
                    <button
                      onClick={() => setRow2Open(false)}
                      className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="row2-close-btn"
                    >
                      <X size={14} /> Close
                    </button>
                  </div>
                  <div className="flex flex-wrap items-stretch gap-1.5">
                    {row2.map((t, i, arr) => renderTabButton(t, i, arr, true))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
          </div>
        )}

        {!wizardActive && showMobileContent && (
          <div className="lg:hidden flex items-center gap-3 mb-3" data-testid="mobile-tab-back-bar">
            <button
              onClick={() => setShowMobileContent(false)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 text-sm font-semibold"
              data-testid="mobile-tab-back-btn"
            >
              <ArrowLeft size={16} /> Back
            </button>
            <div className="flex-1 flex justify-center min-w-0">
              <span className="inline-flex items-center px-4 h-10 rounded-full bg-sky-600 text-white text-[15px] font-bold shadow-sm truncate max-w-full" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="mobile-tab-title">
                {(TABS.find(t => t.key === activeTab) || {}).label}
              </span>
            </div>
            <span className="w-[76px]" aria-hidden="true" />
          </div>
        )}

        <div className={`${!wizardActive && !showMobileContent ? 'hidden lg:block' : 'block'}`} data-testid="dashboard-content">
        {/* While the wizard is active we ONLY render the tab the doctor
            was deep-linked into by tapping a step's CTA. Everything else
            stays mounted in code (lazy) but hidden, keeping context
            switches snappy. */}
        {(!wizardActive || activeTab === 'profile') && activeTab === 'profile' && <ProfileTab specializations={specializations} slug={user.slug} />}
        {(!wizardActive || activeTab === 'leads') && activeTab === 'leads' && <LeadsTab />}
        {!wizardActive && activeTab === 'patient_log' && <PatientLogTab />}
        {!wizardActive && activeTab === 'chat_inbox' && <ChatInboxTab />}
        {!wizardActive && activeTab === 'amounts' && <AmountsTab />}
        {!wizardActive && activeTab === 'dhf_payments' && <DhfPaymentsTab />}
        {(!wizardActive || activeTab === 'locations') && activeTab === 'locations' && <LocationsTab />}
        {(!wizardActive || activeTab === 'schedule') && activeTab === 'schedule' && <ScheduleTab />}
        {!wizardActive && activeTab === 'slots' && <SlotManagerTab />}
        {(!wizardActive || activeTab === 'procedures') && activeTab === 'procedures' && <ProceduresTab />}
        {!wizardActive && activeTab === 'casestudies' && <CaseStudiesTab />}
        {!wizardActive && activeTab === 'tqs' && <TqsTab />}
        {!wizardActive && activeTab === 'seo_articles' && <SeoArticlesTab />}
        {!wizardActive && activeTab === 'clinic_kit' && <ClinicKitTab user={user} />}
        {!wizardActive && activeTab === 'appointments' && <AppointmentsTab />}
        {!wizardActive && activeTab === 'appointment_requests' && <ByAppointmentRequestsTab />}
        {!wizardActive && activeTab === 'wallet' && <WalletTab />}
        {!wizardActive && activeTab === 'visibility' && <VisibilityAcceleratorTab slug={user.slug} />}
        {!wizardActive && activeTab === 'calls_chat' && <CallsChatTab />}
        {!wizardActive && activeTab === 'branding' && <ClinicBrandingTab />}
        </div>
      </div>
      <IncomingCallModal />
    </div>
  );
}
